document.documentElement.classList.remove('no-js');

class GuanyiTheme {
  constructor() {
    this.previousMenuFocus = null;
    this.toastTimer = null;
    this.init(document);
  }

  init(root) {
    this.initMobileMenu(root);
    this.initDisclosures(root);
    this.initQuantity(root);
    this.initProductGallery(root);
    this.initOptionSelectors(root);
    this.initAjaxProductForms(root);
    this.initProductMobileActions(root);
    this.initAccordions(root);
    this.initContactPrefill(root);
    this.initRfqForms(root);
    this.initCollectionControls(root);
    this.initCartForms(root);
    this.initLocalization(root);
    this.initFormStatus(root);
    this.initToast(root);
    this.initHeaderScroll();
  }

  bindOnce(element, key = 'guanyiBound') {
    if (!element || element.dataset[key] === 'true') return false;
    element.dataset[key] = 'true';
    return true;
  }

  getRoutesRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  getFocusable(container) {
    return Array.from(container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => !element.hidden && element.offsetParent !== null);
  }

  initMobileMenu(root) {
    const toggle = root.querySelector?.('[data-mobile-menu-toggle]') || document.querySelector('[data-mobile-menu-toggle]');
    const panel = root.querySelector?.('[data-mobile-menu]') || document.querySelector('[data-mobile-menu]');
    if (!toggle || !panel || !this.bindOnce(panel, 'menuBound')) return;

    const dialog = panel.querySelector('.mobile-nav__panel');
    const closeButtons = panel.querySelectorAll('[data-mobile-menu-close]');

    const setOpen = (open) => {
      panel.classList.toggle('is-open', open);
      document.body.classList.toggle('menu-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      panel.setAttribute('aria-hidden', String(!open));
      panel.inert = !open;
      if (open) {
        this.previousMenuFocus = document.activeElement;
        window.requestAnimationFrame(() => {
          (panel.querySelector('[data-mobile-menu-close]') || dialog)?.focus();
        });
      } else if (this.previousMenuFocus instanceof HTMLElement) {
        this.previousMenuFocus.focus();
      }
    };

    toggle.addEventListener('click', () => setOpen(!panel.classList.contains('is-open')));
    closeButtons.forEach((button) => button.addEventListener('click', () => setOpen(false)));
    panel.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        return;
      }
      if (event.key !== 'Tab' || !panel.classList.contains('is-open')) return;
      const focusable = this.getFocusable(dialog);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  initDisclosures(root) {
    root.querySelectorAll?.('[data-disclosure-toggle]').forEach((button) => {
      if (!this.bindOnce(button, 'disclosureBound')) return;
      const target = document.getElementById(button.getAttribute('aria-controls'));
      if (!target) return;

      const close = (restoreFocus = false) => {
        button.setAttribute('aria-expanded', 'false');
        target.hidden = true;
        document.body.classList.remove('disclosure-open');
        if (restoreFocus) button.focus();
      };

      button.addEventListener('click', () => {
        const isOpen = button.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          close(false);
        } else {
          button.setAttribute('aria-expanded', 'true');
          target.hidden = false;
          document.body.classList.add('disclosure-open');
          window.requestAnimationFrame(() => target.querySelector('input, select, button, a')?.focus());
        }
      });

      target.querySelectorAll('[data-disclosure-close]').forEach((closeButton) => {
        closeButton.addEventListener('click', () => close(true));
      });

      document.addEventListener('click', (event) => {
        if (target.hidden || target.contains(event.target) || button.contains(event.target)) return;
        close(false);
      });

      target.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          close(true);
        }
      });
    });
  }

  initQuantity(root) {
    root.querySelectorAll?.('[data-quantity-wrapper]').forEach((wrapper) => {
      if (!this.bindOnce(wrapper, 'quantityBound')) return;
      const input = wrapper.querySelector('input[type="number"]');
      if (!input) return;
      const step = () => Number(input.step || 1);
      wrapper.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
        const min = Number(input.min || 1);
        const next = Number(input.value || min) - step();
        input.value = String(Math.max(min, next));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      wrapper.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
        const max = input.max ? Number(input.max) : Infinity;
        const next = Number(input.value || 0) + step();
        input.value = String(Math.min(max, next));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      input.addEventListener('change', () => {
        const min = Number(input.min || 1);
        const max = input.max ? Number(input.max) : Infinity;
        const increment = step();
        let value = Number(input.value || min);
        value = Math.min(max, Math.max(min, value));
        value = min + Math.round((value - min) / increment) * increment;
        input.value = String(Math.min(max, Math.max(min, value)));
      });
    });
  }

  initProductGallery(root) {
    root.querySelectorAll?.('[data-product-gallery]').forEach((gallery) => {
      if (!this.bindOnce(gallery, 'galleryBound')) return;
      const mediaItems = Array.from(gallery.querySelectorAll('[data-gallery-media]'));
      const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const targetId = thumb.dataset.mediaId;
          mediaItems.forEach((media) => {
            const active = String(media.dataset.mediaId) === String(targetId);
            if (!active) media.querySelectorAll('video').forEach((video) => video.pause());
            media.hidden = !active;
            media.classList.toggle('is-active', active);
          });
          thumbs.forEach((item) => {
            const active = item === thumb;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', String(active));
          });
        });
      });
    });
  }

  initOptionSelectors(root) {
    root.querySelectorAll?.('[data-option-selectors]').forEach((container) => {
      if (!this.bindOnce(container, 'optionsBound')) return;
      container.querySelectorAll('[data-option-select]').forEach((select) => {
        select.addEventListener('change', async () => {
          const productSection = select.closest('[data-product-section]');
          if (!productSection) return;
          const selectedOption = select.options[select.selectedIndex];
          const basePath = selectedOption?.dataset.productUrl || productSection.dataset.productUrl || window.location.pathname;
          const selectedIds = Array.from(container.querySelectorAll('[data-option-select]'))
            .map((field) => field.options[field.selectedIndex]?.dataset.optionValueId)
            .filter(Boolean);
          const targetUrl = new URL(basePath, window.location.origin);
          if (selectedIds.length) targetUrl.searchParams.set('option_values', selectedIds.join(','));
          targetUrl.searchParams.delete('variant');

          const focusId = select.id;
          const currentProductPath = new URL(productSection.dataset.productUrl || window.location.pathname, window.location.origin).pathname;
          const isCombinedProductSwitch = targetUrl.pathname !== currentProductPath;
          const allDependentRoots = Array.from(document.querySelectorAll('[data-product-section], [data-product-variant-section]'))
            .filter((element) => element.dataset.sectionId);
          const dependentRoots = allDependentRoots.filter((element) => {
            if (isCombinedProductSwitch) return true;
            return element.matches('[data-product-section]')
              || element.matches('.product-engineering-data')
              || element.querySelector('.inquiry-cta');
          });
          const sectionIds = [...new Set(dependentRoots.map((element) => element.dataset.sectionId))].slice(0, 5);
          const requestUrl = new URL(targetUrl.toString());
          requestUrl.searchParams.set('sections', sectionIds.join(','));

          productSection.classList.add('is-loading');
          productSection.setAttribute('aria-busy', 'true');
          productSection.querySelectorAll('select, button, input').forEach((control) => { control.disabled = true; });

          try {
            const response = await fetch(requestUrl.toString(), {
              headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }
            });
            if (!response.ok) throw new Error(`Variant update failed: ${response.status}`);
            const renderedSections = await response.json();
            const replacements = [];

            dependentRoots.forEach((oldRoot) => {
              const id = oldRoot.dataset.sectionId;
              const markup = renderedSections[id];
              if (!markup) return;
              const parsed = new DOMParser().parseFromString(markup, 'text/html');
              const selector = oldRoot.matches('[data-product-section]')
                ? '[data-product-section]'
                : `[data-product-variant-section][data-section-id="${id}"]`;
              const newRoot = parsed.querySelector(selector);
              if (newRoot) replacements.push([oldRoot, newRoot]);
            });

            if (!replacements.some(([oldRoot]) => oldRoot.matches('[data-product-section]'))) {
              throw new Error('Main product section missing from rendered response');
            }

            replacements.forEach(([oldRoot, newRoot]) => oldRoot.replaceWith(newRoot));
            window.history.replaceState({}, '', targetUrl.toString());
            this.init(document);
            window.requestAnimationFrame(() => document.getElementById(focusId)?.focus());
          } catch (error) {
            window.location.assign(targetUrl.toString());
          }
        });
      });
    });
  }

  initAjaxProductForms(root) {
    root.querySelectorAll?.('[data-product-form]').forEach((form) => {
      if (!this.bindOnce(form, 'ajaxCartBound')) return;
      if (form.dataset.ajaxAdd !== 'true') return;

      form.addEventListener('submit', async (event) => {
        const submitter = event.submitter;
        if (!submitter || submitter.name !== 'add') return;
        event.preventDefault();
        if (!form.reportValidity()) return;

        const text = submitter.querySelector('[data-add-button-text]');
        const defaultText = text?.dataset.defaultText || text?.textContent || '';
        const loadingText = text?.dataset.loadingText || defaultText;
        submitter.disabled = true;
        submitter.setAttribute('aria-busy', 'true');
        if (text) text.textContent = loadingText;

        try {
          const response = await fetch(`${this.getRoutesRoot()}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: new FormData(form)
          });
          const payload = await response.json();
          if (!response.ok) throw new Error(payload.description || payload.message || 'Add to cart failed');
          const cartResponse = await fetch(`${this.getRoutesRoot()}cart.js`, { headers: { Accept: 'application/json' } });
          const cart = await cartResponse.json();
          this.updateCartCount(cart.item_count || 0);
          this.showToast(form.closest('[data-product-section]')?.querySelector('.product-info__title')?.textContent?.trim() || payload.product_title || '');
        } catch (error) {
          this.showToast(error.message, true);
        } finally {
          submitter.disabled = false;
          submitter.removeAttribute('aria-busy');
          if (text) text.textContent = defaultText;
        }
      });
    });
  }

  initProductMobileActions(root) {
    root.querySelectorAll?.('[data-product-mobile-actions]').forEach((bar) => {
      if (!this.bindOnce(bar, 'mobileProductBound')) return;
      bar.querySelector('[data-product-mobile-add]')?.addEventListener('click', () => {
        const section = bar.closest('[data-product-section]');
        const form = section?.querySelector('[data-product-form]');
        const addButton = form?.querySelector('button[name="add"]');
        if (form && addButton && !addButton.disabled) form.requestSubmit(addButton);
      });
    });
  }

  updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = count < 1;
    });
  }

  initToast(root) {
    const toast = root.querySelector?.('[data-cart-toast]') || document.querySelector('[data-cart-toast]');
    if (!toast || !this.bindOnce(toast, 'toastBound')) return;
    toast.querySelector('[data-cart-toast-close]')?.addEventListener('click', () => this.hideToast());
  }

  showToast(message, isError = false) {
    const toast = document.querySelector('[data-cart-toast]');
    if (!toast) return;
    const title = toast.querySelector('[data-cart-toast-title]');
    const body = toast.querySelector('[data-cart-toast-message]');
    toast.classList.toggle('is-error', isError);
    if (title) title.textContent = isError ? message : title.dataset.successText || title.textContent;
    if (body) body.textContent = isError ? '' : message;
    toast.hidden = false;
    window.requestAnimationFrame(() => toast.classList.add('is-visible'));
    clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => this.hideToast(), isError ? 7000 : 5000);
  }

  hideToast() {
    const toast = document.querySelector('[data-cart-toast]');
    if (!toast) return;
    toast.classList.remove('is-visible');
    window.setTimeout(() => { toast.hidden = true; }, 220);
  }

  initCollectionControls(root) {
    root.querySelectorAll?.('[data-sort-select]').forEach((select) => {
      if (!this.bindOnce(select, 'sortBound')) return;
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        window.location.assign(url.toString());
      });
    });

    root.querySelectorAll?.('[data-facet-toggle]').forEach((toggle) => {
      if (!this.bindOnce(toggle, 'facetBound')) return;
      const target = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!target) return;
      toggle.addEventListener('click', () => {
        const open = target.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(open));
        if (open) target.querySelector('input, summary, button')?.focus();
      });
    });
  }

  initContactPrefill(root) {
    const field = root.querySelector?.('[data-contact-product]');
    if (!field || !this.bindOnce(field, 'prefillBound')) return;
    const params = new URLSearchParams(window.location.search);
    const values = [params.get('product'), params.get('sku'), params.get('options')].filter(Boolean);
    if (values.length) field.value = values.join(' / ');
  }

  initRfqForms(root) {
    root.querySelectorAll?.('[data-rfq-form]').forEach((container) => {
      if (!this.bindOnce(container, 'rfqBound')) return;
      const form = container.querySelector('form');
      if (!form) return;
      const typeInputs = Array.from(form.querySelectorAll('[data-rfq-type]'));
      const conditionalGroups = Array.from(form.querySelectorAll('[data-rfq-conditional]'));
      const productField = form.querySelector('[data-rfq-product]');
      const quantityField = form.querySelector('[data-rfq-quantity]');
      const productRequiredMark = form.querySelector('[data-rfq-product-required]');
      const quantityRequiredMark = form.querySelector('[data-rfq-quantity-required]');
      const technicalFields = Array.from(form.querySelectorAll('[data-rfq-technical-required-field]'));
      const technicalMarks = Array.from(form.querySelectorAll('[data-rfq-technical-required]'));
      const submit = form.querySelector('[data-rfq-submit]');
      const draftEnabled = container.dataset.rfqDraft === 'true';
      const draftKey = `guanyi-rfq:${window.location.pathname}`;
      let saveTimer = null;

      const setControlsEnabled = (containerElement, enabled) => {
        containerElement.querySelectorAll('input, select, textarea, button').forEach((control) => {
          if (control.matches('[data-rfq-type]')) return;
          control.disabled = !enabled;
        });
      };

      const updateAdvanced = (details) => {
        const enabled = details.open && !details.closest('[hidden]');
        details.querySelectorAll('input, select, textarea').forEach((control) => {
          control.disabled = !enabled;
        });
      };

      const update = () => {
        const selected = typeInputs.find((input) => input.checked);
        const type = selected?.dataset.rfqValue || 'standard';
        conditionalGroups.forEach((group) => {
          const types = (group.dataset.rfqTypes || '').split(',').map((value) => value.trim()).filter(Boolean);
          const active = types.includes(type);
          group.hidden = !active;
          setControlsEnabled(group, active);
          group.querySelectorAll('[data-rfq-advanced]').forEach(updateAdvanced);
        });

        const requiresCommercialFields = type === 'standard' || type === 'custom';
        const requiresTechnicalFields = type === 'selection' || type === 'custom';
        if (productField) productField.required = requiresCommercialFields;
        if (quantityField) quantityField.required = requiresCommercialFields;
        if (productRequiredMark) productRequiredMark.hidden = !requiresCommercialFields;
        if (quantityRequiredMark) quantityRequiredMark.hidden = !requiresCommercialFields;
        technicalFields.forEach((field) => { field.required = requiresTechnicalFields; });
        technicalMarks.forEach((mark) => { mark.hidden = !requiresTechnicalFields; });
      };

      const saveDraft = () => {
        if (!draftEnabled) return;
        const data = {};
        Array.from(form.elements).forEach((field) => {
          if (!field.name || field.disabled || ['hidden', 'submit'].includes(field.type)) return;
          if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) return;
          data[field.name] = field.value;
        });
        try { sessionStorage.setItem(draftKey, JSON.stringify(data)); } catch (_) { /* storage unavailable */ }
      };

      const restoreDraft = () => {
        if (!draftEnabled || container.querySelector('.form-status--success')) return;
        let data = null;
        try { data = JSON.parse(sessionStorage.getItem(draftKey) || 'null'); } catch (_) { data = null; }
        if (!data) return;
        Object.entries(data).forEach(([name, value]) => {
          const fields = Array.from(form.elements).filter((field) => field.name === name);
          fields.forEach((field) => {
            if (field.type === 'radio' || field.type === 'checkbox') field.checked = field.value === value;
            else if (!field.value) field.value = value;
          });
        });
      };

      restoreDraft();
      typeInputs.forEach((input) => input.addEventListener('change', () => { update(); saveDraft(); }));
      form.querySelectorAll('[data-rfq-advanced]').forEach((details) => {
        details.addEventListener('toggle', () => updateAdvanced(details));
      });
      if (draftEnabled) {
        form.addEventListener('input', () => {
          clearTimeout(saveTimer);
          saveTimer = window.setTimeout(saveDraft, 250);
        });
        form.addEventListener('change', saveDraft);
      }
      form.querySelector('[data-rfq-referrer]')?.setAttribute('value', document.referrer || 'Direct');
      form.addEventListener('submit', () => {
        saveDraft();
        if (submit) {
          submit.disabled = true;
          submit.setAttribute('aria-busy', 'true');
          submit.textContent = submit.dataset.loadingText || container.dataset.sendingLabel || submit.textContent;
        }
      });
      if (container.querySelector('.form-status--success')) {
        try { sessionStorage.removeItem(draftKey); } catch (_) { /* storage unavailable */ }
      }
      window.addEventListener('pageshow', () => {
        if (submit) {
          submit.disabled = false;
          submit.removeAttribute('aria-busy');
          submit.textContent = submit.dataset.defaultText || submit.textContent;
        }
      });
      update();
    });
  }

  normalizeCartQuantity(input) {
    let value = Number(input.value || 0);
    if (value <= 0) {
      input.value = '0';
      return 0;
    }
    const min = Number(input.dataset.min || 1);
    const increment = Number(input.dataset.increment || 1);
    const max = input.max ? Number(input.max) : Infinity;
    value = Math.max(min, Math.min(max, value));
    value = min + Math.round((value - min) / increment) * increment;
    value = Math.max(min, Math.min(max, value));
    input.value = String(value);
    return value;
  }

  async updateCartLine(input) {
    const section = input.closest('[data-cart-section]');
    if (!section || section.dataset.ajaxCart !== 'true' || !input.dataset.lineKey) return;
    const quantity = this.normalizeCartQuantity(input);
    const sectionId = section.dataset.sectionId;
    const cartUrl = section.dataset.cartUrl || `${this.getRoutesRoot()}cart`;
    const unsavedNote = section.querySelector('[name="note"]')?.value || '';
    section.classList.add('is-loading');
    section.setAttribute('aria-busy', 'true');
    section.querySelectorAll('button, input, select, textarea').forEach((control) => { control.disabled = true; });

    try {
      const response = await fetch(`${this.getRoutesRoot()}cart/change.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: input.dataset.lineKey,
          quantity,
          sections: [sectionId],
          sections_url: cartUrl
        })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || section.dataset.cartErrorLabel || 'Cart update failed');
      const markup = cart.sections?.[sectionId];
      if (!markup) {
        window.location.assign(cartUrl);
        return;
      }
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const newSection = parsed.querySelector(`[data-cart-section][data-section-id="${sectionId}"]`);
      if (!newSection) {
        window.location.assign(cartUrl);
        return;
      }
      const newNote = newSection.querySelector('[name="note"]');
      if (newNote) newNote.value = unsavedNote;
      section.replaceWith(newSection);
      this.updateCartCount(cart.item_count || 0);
      this.init(document);
      const live = newSection.querySelector('[data-cart-live]');
      if (live) live.textContent = newSection.dataset.cartUpdatedLabel || '';
      window.requestAnimationFrame(() => {
        const nextInput = newSection.querySelector(`[data-cart-quantity][data-line-key="${CSS.escape(input.dataset.lineKey)}"]`);
        const fallback = newSection.querySelector('[data-cart-checkout], .empty-state a, h1');
        if (!nextInput && fallback?.matches('h1')) fallback.setAttribute('tabindex', '-1');
        (nextInput || fallback)?.focus?.();
      });
    } catch (error) {
      section.classList.remove('is-loading');
      section.setAttribute('aria-busy', 'false');
      section.querySelectorAll('button, input, select, textarea').forEach((control) => { control.disabled = false; });
      input.focus();
      this.showToast(error.message || section.dataset.cartErrorLabel || '', true);
    }
  }

  initCartForms(root) {
    root.querySelectorAll?.('[data-cart-form]').forEach((form) => {
      if (!this.bindOnce(form, 'cartFormBound')) return;
      form.querySelectorAll('[data-cart-quantity]').forEach((input) => {
        input.addEventListener('change', () => {
          this.normalizeCartQuantity(input);
          window.clearTimeout(input.guanyiCartUpdateTimer);
          if (input.closest('[data-cart-section]')?.dataset.ajaxCart === 'true') {
            input.guanyiCartUpdateTimer = window.setTimeout(() => this.updateCartLine(input), 450);
          }
        });
      });
      form.addEventListener('submit', (event) => {
        form.querySelectorAll('[data-cart-quantity]').forEach((input) => {
          window.clearTimeout(input.guanyiCartUpdateTimer);
          this.normalizeCartQuantity(input);
        });
        const submitter = event.submitter;
        if (!submitter) return;
        submitter.disabled = true;
        submitter.setAttribute('aria-busy', 'true');
        if (submitter.name === 'update') submitter.textContent = form.dataset.updatingLabel || submitter.textContent;
      });
      window.addEventListener('pageshow', () => {
        form.querySelectorAll('button[disabled][aria-busy="true"]').forEach((button) => {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        });
      });
    });
  }

  initAccordions(root) {
    root.querySelectorAll?.('[data-accordion-button]').forEach((button) => {
      if (!this.bindOnce(button, 'accordionBound')) return;
      button.addEventListener('click', () => {
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        if (!panel) return;
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        panel.hidden = expanded;
      });
    });
  }

  initFormStatus(root) {
    const status = root.querySelector?.('.form-status[tabindex="-1"]');
    if (status && this.bindOnce(status, 'statusFocused')) window.requestAnimationFrame(() => status.focus());
  }

  initHeaderScroll() {
    const header = document.querySelector('.site-header');
    if (!header || header.dataset.scrollBound === 'true') return;
    header.dataset.scrollBound = 'true';
    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 18);
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  initLocalization(root) {
    root.querySelectorAll?.('[data-localization-select]').forEach((select) => {
      if (!this.bindOnce(select, 'localizationBound')) return;
      select.addEventListener('change', () => select.form?.submit());
    });
  }
}

const guanyiTheme = new GuanyiTheme();
document.addEventListener('shopify:section:load', (event) => guanyiTheme.init(event.target));
